import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faEdit,
    faTrash,
    faTimes,
    faCheckCircle,
    faSearch,
    faChevronLeft,
    faChevronRight
} from "@fortawesome/free-solid-svg-icons";

import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../../components/Ui_Element/DotLoader";
import { post, getlist, deleteitem } from "../../../contexts/apicontexts";
import config from "../../../config";

const PAGE_SIZE = 10;

export default function ModuleMenus() {
    const [menus, setMenus] = useState([]);
    const [modules, setModules] = useState([]);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isLoder, setIsLoder] = useState(false);
    const [errors, setErrors] = useState({});
    const [formState, setFormState] = useState({
        id: 0,
        menuName: "",
        userModuleId: 0,
        url: "",
        isVisible: false,
        isEnabled: false,
        sortOrder: 0,
        menuIcon: "",
        hasChild: false,
        parentId: 0
    });
    const validateForm = () => {
        const newErrors = {};

        if (!formState.menuName.trim()) {
            newErrors.menuName = "Menu name is required";
        }

        if (!formState.userModuleId) {
            newErrors.userModuleId = "Module is required";
        }

        if (formState.sortOrder === "" || formState.sortOrder === null) {
            newErrors.sortOrder = "Sort order is required";
        }

        if (!formState.menuIcon.trim()) {
            newErrors.menuIcon = "Menu icon is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        fetchMenus();
        fetchModules();
    }, []);

    const fetchMenus = async () => {
        setIsLoder(true);
        const response = await getlist(`${config.baseurl}ModuleMenus/getlist`);
        if (response?.status === 200) {
            setMenus(response.data || []);
        }
        setIsLoder(false);
    };

    const fetchModules = async () => {
        const response = await getlist(`${config.baseurl}UserModules/getlist`);
        if (response?.status === 200) {
            setModules(response.data || []);
        }
    };

    const openAddModal = () => {
        setFormState({
            id: 0,
            menuName: "",
            userModuleId: 0,
            url: "",
            isVisible: true,
            isEnabled: true,
            sortOrder: 0,
            menuIcon: "",
            hasChild: false,
            parentId: 0
        });
        setIsOpen(true);
    };

    const openEditModal = (menu) => {
        setFormState(menu);
        setIsOpen(true);
    };

    const handleSave = async () => {
        // 1️⃣ Validate form first
        if (!validateForm()) return;

        setIsLoder(true);

        // 2️⃣ Build payload exactly as backend expects
        const payload = {
            id: formState.id || 0,
            userModuleId: Number(formState.userModuleId),
            menuName: formState.menuName.trim(),
            menuIcon: formState.menuIcon || null,
            hasChild: Boolean(formState.hasChild),
            parentId: formState.parentId ? Number(formState.parentId) : 0,
            url: formState.url || null,
            isVisible: Boolean(formState.isVisible),
            isEnabled: Boolean(formState.isEnabled),
            sortOrder: Number(formState.sortOrder) || 0
        };

        try {
            const url = payload.id > 0
                ? `${config.baseurl}ModuleMenus/update`
                : `${config.baseurl}ModuleMenus/add`;

            const response = await post(url, payload);

            if (response?.status === 200) {
                setIsOpen(false);
                setErrors({});
                fetchMenus();
            } else {
                console.error("Save failed:", response);
            }
        } catch (error) {
            console.error("API Error:", error);
        } finally {
            setIsLoder(false);
        }
    };



    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        await deleteitem(`${config.baseurl}UserModuleMenu/DeleteModuleMenu`, id);
        fetchMenus();
    };

    const parentList = menus.filter(x => !x.parentId);

    const filteredMenus = menus.filter((menu) => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return true;

        const menuName = (menu.menuName || "").toLowerCase();
        const moduleName = (menu.userModuleName || "").toLowerCase();
        const url = (menu.url || "").toLowerCase();
        const icon = (menu.menuIcon || "").toLowerCase();

        return (
            menuName.includes(q) ||
            moduleName.includes(q) ||
            url.includes(q) ||
            icon.includes(q)
        );
    });

    const totalPages = Math.max(1, Math.ceil(filteredMenus.length / PAGE_SIZE));

    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginatedData = filteredMenus.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    const getPageButtons = () => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const pages = new Set([1, totalPages, page, page - 1, page + 1]);
        const inRange = Array.from(pages)
            .filter((p) => p >= 1 && p <= totalPages)
            .sort((a, b) => a - b);

        const output = [];
        let prev = 0;
        for (const p of inRange) {
            if (prev && p - prev > 1) output.push("...");
            output.push(p);
            prev = p;
        }
        return output;
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            <Breadcrumb pageName="Module Menus" currentPage="Module Menus" />

            <div className="bg-white rounded-xl shadow border mt-4 overflow-hidden">

                {/* HEADER (APPLIED DESIGN) */}
                <div className="card-header flex justify-between items-center px-5 py-3 border-b transition-colors duration-300
	                    bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]
	                    dark:from-[#2D120D] dark:to-[#1A1A1A] dark:border-gray-800">

                    <div className="flex items-center gap-3">
                        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                            Module Menu List
                        </h2>
                        {isLoder && <DotLoader color="#ffffff" size="w-1.5 h-1.5" />}
                    </div>

                    <div className="flex items-center gap-3 w-full max-w-xl justify-end">
                        <div className="relative w-full max-w-sm hidden md:block">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search menu/module/url/icon..."
                                className="w-full pl-10 pr-4 py-2 rounded-full border-none outline-none focus:ring-2 focus:ring-white/50 text-sm
                                    bg-white/10 text-white placeholder-white/60 backdrop-blur-sm transition-all"
                            />
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/60"
                            />
                        </div>
                        <button
                            onClick={openAddModal}
                            className="flex items-center justify-center w-10 h-10 rounded-full transition-all
	                            transform hover:scale-110 active:scale-95 shadow-lg
	                            bg-white dark:bg-gray-200 text-[#C04C2A] dark:text-[#4A1B13]"
                        >
                            <FontAwesomeIcon icon={faPlus} className="text-lg" />
                        </button>
                    </div>
                </div>

                {/* TABLE */}
                <div className="p-4 md:hidden">
                    <div className="relative w-full">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search menu/module/url/icon..."
                            className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-200 outline-none focus:ring-2 focus:ring-orange-100 text-sm
                                bg-white text-gray-800 placeholder-gray-400 transition-all"
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-3 text-left">Menu</th>
                                <th className="p-3">Module</th>
                                <th className="p-3">Order</th>
                                <th className="p-3">Visible</th>
                                <th className="p-3">Enabled</th>
                                <th className="p-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map(menu => (
                                    <tr key={menu.id} className="border-t hover:bg-gray-50">
                                        <td className="p-3">{menu.menuName}</td>
                                        <td className="p-3">{menu.userModuleName}</td>
                                        <td className="p-3 text-center">{menu.sortOrder}</td>
                                        <td className="p-3 text-center">{menu.isVisible ? "Yes" : "No"}</td>
                                        <td className="p-3 text-center">{menu.isEnabled ? "Yes" : "No"}</td>
                                        <td className="p-3 text-right space-x-3">
                                            <button onClick={() => openEditModal(menu)}>
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(menu.id)}
                                                className="text-red-600"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-6 text-center text-gray-400">
                                        No module menus found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {filteredMenus.length > 0 && totalPages > 1 && (
                    <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                        <div className="text-xs sm:text-sm text-gray-600">
                            Showing <span className="font-bold text-gray-800">{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                            <span className="font-bold text-gray-800">{Math.min(page * PAGE_SIZE, filteredMenus.length)}</span> of{" "}
                            <span className="font-bold text-gray-800">{filteredMenus.length}</span> menus
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                disabled={page === 1}
                                className="p-2 rounded-lg border bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                            </button>
                            {getPageButtons().map((p, idx) => (
                                p === "..." ? (
                                    <span key={`e-${idx}`} className="px-2 text-gray-400 select-none">...</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        className={`w-9 h-9 rounded-lg text-sm font-bold transition-all shadow-sm ${page === p
                                            ? "bg-gradient-to-r from-[#C04C2A] to-[#4A1B13] text-white shadow-[#C04C2A]/20"
                                            : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                            }`}
                                    >
                                        {p}
                                    </button>
                                )
                            ))}
                            <button
                                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={page === totalPages}
                                className="p-2 rounded-lg border bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                <FontAwesomeIcon icon={faChevronRight} size="sm" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {isOpen && (

                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setIsOpen(false)}
                    ></div>

                    {/* Modal */}
                    <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl p-8 relative shadow-2xl
                     transform transition-all animate-in fade-in zoom-in duration-200">

                        {/* Close */}
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                        >
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                        </button>

                        {/* Title */}
                        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
                            {formState.id ? "Update Menu" : "Create New Menu"}
                        </h2>

                        {/* Form */}
                        <div className="space-y-5">

                            {/* Menu Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Menu Name
                                </label>
                                <input
                                    className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                            outline-none focus:ring-2
                                            ${errors.menuName
                                            ? "border-red-500 ring-red-100"
                                            : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                        }`}
                                    value={formState.menuName}
                                    onChange={e => {
                                        setFormState({ ...formState, menuName: e.target.value });
                                        setErrors({ ...errors, menuName: "" });
                                    }}
                                />
                                {errors.menuName && (
                                    <p className="mt-1 text-xs text-red-500 font-medium">{errors.menuName}</p>
                                )}

                            </div>

                            {/* Module */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Module
                                </label>
                                <select
                                    className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                            outline-none focus:ring-2
                                            ${errors.userModuleId
                                            ? "border-red-500 ring-red-100"
                                            : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                        }`}
                                    value={formState.userModuleId}
                                    onChange={e => {
                                        setFormState({ ...formState, userModuleId: Number(e.target.value) });
                                        setErrors({ ...errors, userModuleId: "" });
                                    }}
                                >

                                    <option value="">Select Module</option>
                                    {modules.map(m => (
                                        <option key={m.id} value={m.id}>{m.moduleName}</option>
                                    ))}
                                </select>
                                {errors.userModuleId && (
                                    <p className="mt-1 text-xs text-red-500 font-medium">{errors.userModuleId}</p>
                                )}
                            </div>

                            {/* Icon + Sort */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Menu Icon
                                    </label>
                                    <input
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                                outline-none focus:ring-2
                                                ${errors.menuIcon
                                                ? "border-red-500 ring-red-100"
                                                : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                            }`}
                                        value={formState.menuIcon}
                                        onChange={e => {
                                            setFormState({ ...formState, menuIcon: e.target.value });
                                            setErrors({ ...errors, menuIcon: "" });
                                        }}
                                    />
                                    {errors.menuIcon && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{errors.menuIcon}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Sort Order
                                    </label>
                                    <input
                                        type="number"
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                                outline-none focus:ring-2
                                                ${errors.sortOrder
                                                ? "border-red-500 ring-red-100"
                                                : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                            }`}
                                        value={formState.sortOrder}
                                        onChange={e => {
                                            setFormState({ ...formState, sortOrder: e.target.value });
                                            setErrors({ ...errors, sortOrder: "" });
                                        }}
                                    />
                                    {errors.sortOrder && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{errors.sortOrder}</p>
                                    )}
                                </div>
                            </div>

                            {/* Menu Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Url
                                </label>
                                <input
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                        outline-none focus:ring-2 border-gray-400 dark:border-gray-700
                        focus:ring-orange-100 focus:border-[#C04C2A]"
                                    value={formState.url}
                                    onChange={e => setFormState({ ...formState, url: e.target.value })}
                                />
                            </div>
                            {/* Toggles */}
                            <div className="space-y-3">
                                {[
                                    { label: "Visible", key: "isVisible" },
                                    { label: "Enabled", key: "isEnabled" },
                                    { label: "Has Child", key: "hasChild" }
                                ].map(item => (
                                    <div
                                        key={item.key}
                                        className="flex items-center p-4 bg-gray-100 dark:bg-gray-800 rounded-xl
                            border border-gray-100 dark:border-gray-700"
                                    >
                                        <label className="relative flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formState[item.key]}
                                                onChange={e =>
                                                    setFormState({
                                                        ...formState,
                                                        [item.key]: e.target.checked
                                                    })
                                                }
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-300 rounded-full peer dark:bg-gray-700
                                    peer-checked:bg-[#C04C2A]
                                    after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                                    after:bg-white after:border after:rounded-full after:h-5 after:w-5
                                    after:transition-all peer-checked:after:translate-x-full">
                                            </div>
                                            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {item.label}
                                            </span>
                                        </label>
                                    </div>
                                ))}
                            </div>

                            {/* Parent Menu */}
                            {formState.hasChild && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Parent Menu
                                    </label>
                                    <select
                                        className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                            outline-none focus:ring-2 border-gray-400 dark:border-gray-700
                            focus:ring-orange-100 focus:border-[#C04C2A]"
                                        value={formState.parentId}
                                        onChange={e =>
                                            setFormState({ ...formState, parentId: Number(e.target.value) })
                                        }
                                    >
                                        <option value="">Select Parent</option>
                                        {parentList.map(p => (
                                            <option key={p.id} value={p.id}>{p.menuName}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700
                        text-gray-600 dark:text-gray-300 font-bold rounded-xl
                        hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={isLoder}
                                    className="flex-1 px-4 py-3 text-white font-bold rounded-xl
                        shadow-lg transform transition-all active:scale-95 disabled:opacity-70"
                                    style={{
                                        background: "linear-gradient(to right, #C04C2A, #4A1B13)"
                                    }}
                                >
                                    {isLoder ? (
                                        <DotLoader color="#fff" size="w-1.5 h-1.5" />
                                    ) : (
                                        formState.id ? "Update Menu" : "Save Menu"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
}
